import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PortalPageHeader } from "@/components/portal-page-header";
import { parseClassIdParams } from "@/lib/route-params";
import { useTeacherClassesGetTeacherClassDetail } from "@/orval/teacher-classes";

export const Route = createFileRoute("/_app/organizations/$organizationId/teacher/classes_/$classId")({
  params: {
    parse: parseClassIdParams,
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId } = Route.useParams();
  const { data: classDetail } = useTeacherClassesGetTeacherClassDetail(organizationId, classId);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isEditingUpdate =
    pathname !== `/organizations/${organizationId}/teacher/classes/${classId}/updates` &&
    pathname.startsWith(`/organizations/${organizationId}/teacher/classes/${classId}/updates/`);
  const activeTab = getActiveClassTab(pathname);

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden p-4 md:p-6">
      {!isEditingUpdate ? (
        <TeacherClassHeader
          organizationId={organizationId}
          classId={classId}
          className={classDetail?.name}
          activeTab={activeTab}
        />
      ) : null}
      <Outlet />
    </div>
  );
}

export function TeacherClassHeader({
  organizationId,
  classId,
  className,
  activeTab,
}: {
  organizationId: number;
  classId: number;
  className?: string;
  activeTab: "details" | "roster" | "updates";
}) {
  return (
    <PortalPageHeader
      className="mb-6"
      breadcrumbs={[
        {
          label: "Classes",
          to: "/organizations/$organizationId/teacher/classes",
          params: { organizationId },
        },
        { label: className ?? "Class Details" },
      ]}
      title={className ?? "Class Details"}
      subtitle="View class information, roster, and updates"
      actions={
        <Tabs value={activeTab}>
          <TabsList>
            <TabsTrigger value="details" asChild>
              <Link to="/organizations/$organizationId/teacher/classes/$classId" params={{ organizationId, classId }}>
                Details
              </Link>
            </TabsTrigger>
            <TabsTrigger value="roster" asChild>
              <Link
                to="/organizations/$organizationId/teacher/classes/$classId/roster"
                params={{ organizationId, classId }}
              >
                Roster
              </Link>
            </TabsTrigger>
            <TabsTrigger value="updates" asChild>
              <Link
                to="/organizations/$organizationId/teacher/classes/$classId/updates"
                params={{ organizationId, classId }}
              >
                Updates
              </Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    />
  );
}

function getActiveClassTab(pathname: string): "roster" | "updates" | "details" {
  if (pathname.endsWith("/roster")) {
    return "roster";
  }
  return pathname.includes("/updates") ? "updates" : "details";
}
